import * as Toast from "@radix-ui/react-toast";
import { useCallback, useState, type ReactNode } from "react";
import { ToastContext, type ToastApi } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";
import { cn } from "../../lib/cn.js";

interface ToastItem {
  id: number;
  title: string;
  description?: string;
  tone: "success" | "danger";
  open: boolean;
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const { t, locale } = useI18n();

  const dismiss = useCallback((id: number) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, open: false } : item))
    );
  }, []);

  const push = useCallback<ToastApi["push"]>((title, description, tone = "success") => {
    setItems((current) => [
      ...current,
      { id: nextId++, title, description, tone, open: true },
    ]);
  }, []);

  return (
    <Toast.Provider swipeDirection={locale === "ar" ? "left" : "right"}>
      <ToastContext.Provider value={{ push }}>{children}</ToastContext.Provider>
      {items.map((item) => (
        <Toast.Root
          key={item.id}
          open={item.open}
          onOpenChange={(open) => {
            if (!open) dismiss(item.id);
          }}
          duration={5000}
          className={cn(
            "flex items-start gap-3 rounded-[var(--radius-card)] border bg-surface-raised px-4 py-3 shadow-md",
            item.tone === "success"
              ? "border-status-complete/40"
              : "border-action-danger/40"
          )}
        >
          <div className="flex-1">
            <Toast.Title className="text-sm font-semibold text-ink">
              {item.title}
            </Toast.Title>
            {item.description ? (
              <Toast.Description className="mt-0.5 text-sm text-ink-muted">
                {item.description}
              </Toast.Description>
            ) : null}
          </div>
        </Toast.Root>
      ))}
      <Toast.Viewport
        className="fixed bottom-4 end-4 z-50 flex w-[min(24rem,90vw)] flex-col gap-2 outline-none"
        aria-label={t("common.notifications")}
      />
    </Toast.Provider>
  );
}
